import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  FileDown,
  Loader2,
  Plus,
  Search,
  Trash2,
  UserMinus,
  UserPlus,
  Users,
} from 'lucide-react';
import {
  createEquipe,
  deleteEquipe,
  fetchEquipes,
  fetchSaisons,
  updateComposition,
  updateEquipe,
  urlFeuilleEquipe,
} from '../services/api';
import { useAuth } from '../hooks/useAuth';

type Notice = { type: 'success' | 'error'; message: string } | null;

/**
 * Les équipes du club et leur composition.
 *
 * Une équipe n'est pas une catégorie d'âge : un club a deux équipes U15 quand
 * il a assez de joueurs, et une seule qui mélange U13 et U15 quand il n'en a
 * pas assez. L'entraîneur compose ici, et emporte sa feuille en PDF — celle
 * qu'il tend à l'arbitre le dimanche.
 */
export default function EquipesClub() {
  const { aLeDroit } = useAuth();
  const queryClient = useQueryClient();
  const peutGerer = aLeDroit('gerer_adherents');

  const [saison, setSaison] = useState<string>('');
  const [ouverte, setOuverte] = useState<number | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const [creation, setCreation] = useState(false);
  const [form, setForm] = useState({ nom: '', discipline: '', categorie: '', engagement: '', entraineurId: '' });

  const saisonsQuery = useQuery({ queryKey: ['saisons'], queryFn: fetchSaisons });
  const saisons: any[] = saisonsQuery.data?.saisons ?? [];

  const query = useQuery({
    queryKey: ['equipes', saison],
    queryFn: () => fetchEquipes(saison || undefined),
  });

  /*
    QUI PEUT ÊTRE ALIGNÉ, ET DANS QUELLE DISCIPLINE.

    On composait depuis le registre entier — tous les adhérents, licenciés ou
    non. Deux conséquences, dont la seconde est grave :

    - un entraîneur de gymnastique faisait défiler quatre-vingts footballeurs
      pour retrouver ses gymnastes ;
    - rien n'empêchait d'inscrire sur une feuille de match quelqu'un qui n'a
      PAS de licence cette saison, c'est-à-dire quelqu'un qui n'est pas assuré.
      Cela ne se découvre qu'après la blessure.

    Le serveur rend maintenant les licenciés de la saison affichée, avec leur
    discipline et leur reste dû.
  */
  const licencies: any[] = query.data?.licencies ?? [];
  const disciplines: any[] = query.data?.disciplines ?? [];

  const annoncer = (type: 'success' | 'error', message: string) => {
    setNotice({ type, message });
    if (type === 'success') setTimeout(() => setNotice(null), 3000);
  };

  const rafraichir = () => queryClient.invalidateQueries({ queryKey: ['equipes'] });

  const creer = useMutation({
    /*
      « discipline » du formulaire porte désormais un IDENTIFIANT, pas un mot.

      On l'envoie donc sous le nom que le serveur attend — sportPreset — et non
      sous l'ancien. Laisser les deux ferait qu'une équipe créée aujourd'hui
      s'étiquetterait « 3 » au lieu de « Football ».
    */
    mutationFn: () =>
      createEquipe({
        nom: form.nom,
        categorie: form.categorie,
        engagement: form.engagement,
        entraineurId: form.entraineurId,
        sportPreset: form.discipline === '' ? null : Number(form.discipline),
        saison: saison || undefined,
      }),
    onSuccess: (d: any) => {
      annoncer('success', d.message);
      setCreation(false);
      setForm({ nom: '', discipline: '', categorie: '', engagement: '', entraineurId: '' });
      rafraichir();
      setOuverte(d.equipe?.id ?? null);
    },
    onError: (e: Error) => annoncer('error', e.message),
  });

  const dissoudre = useMutation({
    mutationFn: (id: number) => deleteEquipe(id),
    onSuccess: (d: any) => {
      annoncer('success', d.message);
      rafraichir();
    },
    onError: (e: Error) => annoncer('error', e.message),
  });

  if (query.isPending || saisonsQuery.isPending) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  const equipes: any[] = query.data?.equipes ?? [];
  const config = query.data?.configuration;
  const encadrants: any[] = query.data?.encadrants ?? [];

  if (saisons.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-dashed border-gray-300 p-10 text-center text-sm text-gray-500">
        <Users className="w-8 h-8 mx-auto text-gray-300 mb-3" />
        Aucune saison n'est ouverte. Créez-en une dans Administration → Adhérents avant de composer une
        équipe.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {notice && (
        <div
          className={`rounded-lg px-4 py-2 text-sm ${
            notice.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {notice.message}
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-center gap-3">
        <select
          value={saison}
          onChange={(e) => setSaison(e.target.value)}
          className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
        >
          {saisons.map((s) => (
            <option key={s.id} value={s.id}>
              Saison {s.libelle}
              {s.courante ? ' (courante)' : ''}
            </option>
          ))}
        </select>
        <span className="text-xs text-gray-500">
          {equipes.length} équipe{equipes.length > 1 ? 's' : ''}
        </span>
        {peutGerer && !creation && (
          <button
            onClick={() => setCreation(true)}
            className="ml-auto inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700"
          >
            <Plus className="w-4 h-4" />
            Nouvelle équipe
          </button>
        )}
      </div>

      {creation && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              autoFocus
              value={form.nom}
              onChange={(e) => setForm({ ...form, nom: e.target.value })}
              placeholder="Nom de l'équipe — « U15 Filles », « Séniors 2 » *"
              className="sm:col-span-2 px-3 py-2 rounded-lg border border-gray-300 text-sm"
            />
{/*
              LA DISCIPLINE VIENT DES SECTIONS EN SERVICE, PAS D'UNE LISTE DE MOTS.

              Ce menu proposait les « disciplines » saisies en texte libre dans
              les réglages du registre. Elles ne sont rattachées à rien : une
              équipe étiquetée « Foot » n'a aucun lien avec la section football,
              donc aucun moyen de savoir qui est licencié dedans.

              Il propose maintenant les disciplines réellement en service, et
              c'est ce lien qui décide de qui sera proposé à la composition.

              À une seule discipline, pas de menu : le serveur rattache la
              sienne, et un choix qui n'en est pas un fait perdre du temps.
            */}
            {disciplines.length > 1 && (
              <select
                value={form.discipline}
                onChange={(e) => setForm({ ...form, discipline: e.target.value })}
                className="px-3 py-2 rounded-lg border border-gray-300 text-sm"
              >
                <option value="">Discipline…</option>
                {disciplines.map((d: any) => (
                  <option key={d.id} value={String(d.id)}>
                    {d.nom}
                  </option>
                ))}
              </select>
            )}
            <select
              value={form.categorie}
              onChange={(e) => setForm({ ...form, categorie: e.target.value })}
              className="px-3 py-2 rounded-lg border border-gray-300 text-sm"
            >
              <option value="">Catégorie visée…</option>
              {(config?.categories ?? []).map((c: any) => (
                <option key={c.code} value={c.libelle}>
                  {c.libelle}
                </option>
              ))}
            </select>
            <select
              value={form.entraineurId}
              onChange={(e) => setForm({ ...form, entraineurId: e.target.value })}
              className="px-3 py-2 rounded-lg border border-gray-300 text-sm"
            >
              <option value="">Encadrant…</option>
              {encadrants.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.nom} {m.fonction ? `— ${m.fonction}` : ''}
                </option>
              ))}
            </select>
            <input
              value={form.engagement}
              onChange={(e) => setForm({ ...form, engagement: e.target.value })}
              placeholder="Championnat, poule, gymnase"
              className="px-3 py-2 rounded-lg border border-gray-300 text-sm"
            />
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={() => setCreation(false)} className="px-3 py-1.5 text-sm text-gray-600">
              Annuler
            </button>
            <button
              onClick={() => creer.mutate()}
              disabled={creer.isPending || form.nom.trim() === ''}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-60"
            >
              {creer.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Créer
            </button>
          </div>
        </div>
      )}

      {equipes.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-gray-300 p-10 text-center text-sm text-gray-500">
          <Users className="w-8 h-8 mx-auto text-gray-300 mb-3" />
          Aucune équipe pour cette saison.
        </div>
      ) : (
        <div className="space-y-3">
          {equipes.map((equipe) => (
            <CarteEquipe
              key={equipe.id}
              equipe={equipe}
              licencies={licencies}
              disciplines={disciplines}
              configuration={config}
              encadrants={encadrants}
              ouverte={ouverte === equipe.id}
              onBasculer={() => setOuverte(ouverte === equipe.id ? null : equipe.id)}
              peutGerer={peutGerer}
              onAnnoncer={annoncer}
              onDissoudre={() => {
                if (window.confirm(`Dissoudre l'équipe ${equipe.nom} ?`)) dissoudre.mutate(equipe.id);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Une équipe, dépliable sur sa composition. */
function CarteEquipe({
  equipe,
  configuration,
  encadrants,
  licencies,
  disciplines,
  ouverte,
  onBasculer,
  peutGerer,
  onAnnoncer,
  onDissoudre,
}: {
  equipe: any;
  configuration: any;
  encadrants: any[];
  /** Les licenciés de la saison — une entrée par licence, donc par discipline. */
  licencies: any[];
  disciplines: any[];
  ouverte: boolean;
  onBasculer: () => void;
  peutGerer: boolean;
  onAnnoncer: (type: 'success' | 'error', message: string) => void;
  onDissoudre: () => void;
}) {
  const queryClient = useQueryClient();
  const [composition, setComposition] = useState<any[]>(equipe.composition ?? []);
  const [recherche, setRecherche] = useState('');

  // La composition locale suit celle du serveur tant qu'on n'y a pas touché.
  useEffect(() => {
    setComposition(equipe.composition ?? []);
  }, [equipe.composition]);

  const enregistrer = useMutation({
    mutationFn: () =>
      updateComposition(
        equipe.id,
        composition.map((c, i) => ({
          adherentId: c.adherentId,
          role: c.role ?? '',
          numero: c.numero ?? '',
          ordre: i,
        }))
      ),
    onSuccess: (d: any) => {
      onAnnoncer('success', d.message);
      queryClient.invalidateQueries({ queryKey: ['equipes'] });
    },
    onError: (e: Error) => onAnnoncer('error', e.message),
  });

  const modifierEquipe = useMutation({
    mutationFn: (donnees: Record<string, unknown>) => updateEquipe(equipe.id, donnees),
    onSuccess: (d: any) => {
      onAnnoncer('success', d.message);
      queryClient.invalidateQueries({ queryKey: ['equipes'] });
    },
    onError: (e: Error) => onAnnoncer('error', e.message),
  });

  const inscrits = useMemo(
    () => new Set(composition.map((c) => c.adherentId)),
    [composition]
  );

  /*
    LES LICENCIÉS DE LA DISCIPLINE DE CETTE ÉQUIPE, MOINS CEUX DÉJÀ INSCRITS.

    Une équipe sans discipline — un club mono-discipline, ou une équipe créée
    avant le module — les prend tous : filtrer sur rien ne montrerait personne.

    Le même adhérent apparaît une fois par licence : on le dédoublonne, puisque
    la composition porte sur la personne et non sur sa licence.
  */
  const disponibles: any[] = useMemo(() => {
    const cherche = recherche.trim().toLowerCase();
    const vus = new Set<number>();
    const retenus: any[] = [];

    for (const l of licencies) {
      if (inscrits.has(l.adherentId) || vus.has(l.adherentId)) {
        continue;
      }

      if (
        equipe.disciplineId != null &&
        l.disciplineId != null &&
        l.disciplineId !== equipe.disciplineId
      ) {
        continue;
      }

      if (cherche.length >= 2 && !String(l.nomComplet).toLowerCase().includes(cherche)) {
        continue;
      }

      vus.add(l.adherentId);
      retenus.push(l);
    }

    return retenus;
  }, [licencies, inscrits, equipe.disciplineId, recherche]);
  const roles: string[] = configuration?.rolesEquipe ?? [];

  const ajouter = (licencie: any) =>
    setComposition([
      ...composition,
      {
        adherentId: licencie.adherentId,
        adherent: licencie.nomComplet,
        age: licencie.age,
        role: '',
        numero: '',
      },
    ]);

  const modifierLigne = (index: number, patch: Record<string, string>) =>
    setComposition(composition.map((c, i) => (i === index ? { ...c, ...patch } : c)));

  const modifie = JSON.stringify(composition.map((c) => [c.adherentId, c.role, c.numero]))
    !== JSON.stringify((equipe.composition ?? []).map((c: any) => [c.adherentId, c.role, c.numero]));

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 p-4">
        <button onClick={onBasculer} className="flex items-center gap-2 min-w-0 flex-1 text-left">
          {ouverte ? (
            <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
          )}
          <span className="min-w-0">
            <span className="block font-semibold text-gray-900 truncate">{equipe.nom}</span>
            <span className="block text-xs text-gray-500 truncate">
              {[equipe.categorie, equipe.discipline, equipe.engagement, equipe.entraineur]
                .filter(Boolean)
                .join(' · ') || 'Sans détail'}
            </span>
          </span>
        </button>

        <span className="shrink-0 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-700">
          {equipe.effectif} joueur{equipe.effectif > 1 ? 's' : ''}
        </span>

        {equipe.effectif > 0 && (
          <a
            href={urlFeuilleEquipe(equipe.id)}
            target="_blank"
            rel="noreferrer"
            title="Feuille d'équipe en PDF"
            className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-primary-200 text-primary-700 text-sm font-medium hover:bg-primary-50"
          >
            <FileDown className="w-4 h-4" />
            Feuille
          </a>
        )}

        {peutGerer && (
          <button
            onClick={onDissoudre}
            title="Dissoudre l'équipe"
            className="shrink-0 p-1.5 rounded text-red-600 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {ouverte && (
        <div className="border-t border-gray-100 p-4 space-y-4">
          {/*
            L'AVERTISSEMENT LÀ OÙ IL COMPTE : AU MOMENT DE COMPOSER.

            Une équipe sans discipline propose TOUS les licenciés du club au
            lieu de ceux de sa section. Ce n'est pas grave dans un club qui n'en
            a qu'une — c'est même le bon comportement — et le message ne
            paraît donc qu'à partir de deux.

            Le tableau de bord le signale déjà, mais on ne compose pas depuis le
            tableau de bord : on compose ici, et c'est ici qu'on se demande
            pourquoi la liste propose des gymnastes à une équipe de football.
          */}
          {disciplines.length > 1 && equipe.disciplineId == null && (
            <p className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                Cette équipe n'est rattachée à aucune discipline : la liste ci-dessous vous propose
                <strong> tous les licenciés du club</strong>, et non ceux de sa section.
                {peutGerer && ' Choisissez-la dans le menu ci-dessous.'}
              </span>
            </p>
          )}

          {peutGerer && (
            <div className="flex flex-wrap items-center gap-2">
              {/*
                LA DISCIPLINE, ET CE QU'ELLE DÉCIDE.

                Elle ne sert pas qu'à étiqueter : c'est elle qui décide de QUI
                sera proposé plus bas. Une équipe rattachée à la gymnastique ne
                propose que les gymnastes ; une équipe sans discipline propose
                tous les licenciés, ce qui est le bon comportement pour un club
                qui n'en a qu'une.

                Le menu n'apparaît qu'à partir de deux disciplines : ailleurs il
                n'y a rien à départager, et le serveur rattache la seule en
                service.
              */}
              {disciplines.length > 1 && (
                <select
                  value={equipe.disciplineId ?? ''}
                  onChange={(e) => modifierEquipe.mutate({ sportPreset: e.target.value })}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
                  title="Décide des licenciés proposés à la composition"
                >
                  <option value="">Sans discipline</option>
                  {disciplines.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.nom}
                    </option>
                  ))}
                </select>
              )}
              <select
                value={equipe.entraineurId ?? ''}
                onChange={(e) => modifierEquipe.mutate({ entraineurId: e.target.value })}
                className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
              >
                <option value="">Sans encadrant</option>
                {encadrants.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.nom}
                  </option>
                ))}
              </select>
              <input
                defaultValue={equipe.engagement ?? ''}
                onBlur={(e) => {
                  if (e.target.value !== (equipe.engagement ?? '')) {
                    modifierEquipe.mutate({ engagement: e.target.value });
                  }
                }}
                placeholder="Championnat, poule, gymnase"
                className="flex-1 min-w-[12rem] px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
              />
            </div>
          )}

          {/* La composition */}
          {composition.length === 0 ? (
            <p className="text-sm text-gray-500">Personne dans cette équipe pour l'instant.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {composition.map((c, index) => (
                <li key={c.adherentId} className="flex flex-wrap items-center gap-2 py-2">
                  <span className="w-6 text-xs text-gray-400 tabular-nums">{index + 1}</span>
                  <span className="flex-1 min-w-[8rem] text-sm text-gray-900">
                    {c.adherent}
                    {c.age !== null && c.age !== undefined && (
                      <span className="text-gray-400"> · {c.age} ans</span>
                    )}
                  </span>
                  <input
                    value={c.numero ?? ''}
                    onChange={(e) => modifierLigne(index, { numero: e.target.value })}
                    placeholder="N°"
                    disabled={!peutGerer}
                    className="w-16 px-2 py-1 rounded-lg border border-gray-300 text-sm text-center disabled:bg-gray-100"
                  />
                  <select
                    value={c.role ?? ''}
                    onChange={(e) => modifierLigne(index, { role: e.target.value })}
                    disabled={!peutGerer}
                    className="px-2 py-1 rounded-lg border border-gray-300 text-sm disabled:bg-gray-100"
                  >
                    <option value="">Rôle…</option>
                    {roles.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                  {peutGerer && (
                    <button
                      onClick={() => setComposition(composition.filter((_, i) => i !== index))}
                      title="Retirer de l'équipe"
                      className="p-1.5 rounded text-red-600 hover:bg-red-50"
                    >
                      <UserMinus className="w-4 h-4" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {peutGerer && (
            <>
              <div className="border-t border-gray-100 pt-3">
                <div className="relative mb-2">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    value={recherche}
                    onChange={(e) => setRecherche(e.target.value)}
                    placeholder="Chercher un adhérent à ajouter"
                    className="w-full pl-9 pr-3 py-1.5 rounded-lg border border-gray-300 text-sm"
                  />
                </div>
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {disponibles.slice(0, 40).map((a: any) => (
                    <button
                      key={a.id}
                      onClick={() => ajouter(a)}
                      className="w-full flex items-center gap-2 rounded-lg px-3 py-1.5 text-left text-sm hover:bg-primary-50"
                    >
                      <UserPlus className="w-4 h-4 text-primary-600 shrink-0" />
                      <span className="flex-1 min-w-0 truncate">{a.nomComplet}</span>
                      <span className="text-xs text-gray-400 shrink-0">
                        {[a.categorie, a.age !== null ? `${a.age} ans` : null].filter(Boolean).join(' · ')}
                      </span>
                    </button>
                  ))}
                  {disponibles.length === 0 && (
                    <p className="text-sm text-gray-400 px-3 py-2">
                      Tout le monde est déjà dans l'équipe, ou la recherche ne rend personne.
                    </p>
                  )}
                </div>
              </div>

              {modifie && (
                <div className="flex items-center gap-3 border-t border-gray-100 pt-3">
                  <button
                    onClick={() => enregistrer.mutate()}
                    disabled={enregistrer.isPending}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-60"
                  >
                    {enregistrer.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                    Enregistrer la composition
                  </button>
                  <button
                    onClick={() => setComposition(equipe.composition ?? [])}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Annuler
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
